import type { LoginRequest, LoginResult, SessionUser } from "@rema/shared"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { es } from "../i18n/es"
import {
  ApiError,
  apiFetch,
  makeProblem,
  refreshSession,
  setAccessToken,
  setSessionEvents,
} from "../lib/api"
import { queryClient } from "../lib/query"

// Sesión de la consola — docs/05 §7: access token solo en memoria; el refresh
// vive en la cookie httpOnly rema_rt (contrato de sesión, cookie: true en login).

export type SessionStatus = "loading" | "authenticated" | "anonymous"

interface SessionContextValue {
  status: SessionStatus
  user: SessionUser | null
  scopes: readonly string[]
  hasScope: (scope: string) => boolean
  login: (identifier: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export interface SessionProviderProps {
  children: ReactNode
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [status, setStatus] = useState<SessionStatus>("loading")
  const [user, setUser] = useState<SessionUser | null>(null)

  useEffect(() => {
    // Los listeners se registran ANTES del bootstrap para capturar su resultado;
    // también mantienen la sesión al día cuando el auto-refresh por 401 rota tokens.
    setSessionEvents({
      onRefreshed: (response) => {
        setUser(response.user)
        setStatus("authenticated")
      },
      onExpired: () => {
        setUser(null)
        setStatus("anonymous")
      },
    })
    // Bootstrap silencioso: si hay cookie rema_rt válida, restaura la sesión
    void refreshSession().then((ok) => {
      if (!ok) setStatus("anonymous")
    })
    return () => setSessionEvents({})
  }, [])

  const login = useCallback(async (identifier: string, password: string) => {
    const body: LoginRequest = { identifier, password, cookie: true }
    const result = await apiFetch<LoginResult>("/auth/login", {
      method: "POST",
      body,
      retryOn401: false,
    })
    if ("mfa_required" in result) {
      // MFA es V1: la consola aún no implementa el segundo paso
      throw new ApiError(makeProblem("mfa_required", 401, es.errors.mfa_required))
    }
    setAccessToken(result.access_token)
    setUser(result.user)
    setStatus("authenticated")
  }, [])

  const logout = useCallback(async () => {
    try {
      // Revoca la familia vía cookie rema_rt y la limpia (contrato de sesión)
      await apiFetch<undefined>("/auth/logout", { method: "POST", body: {}, retryOn401: false })
    } catch {
      // La sesión local se limpia aunque la revocación remota falle
    }
    setAccessToken(null)
    setUser(null)
    setStatus("anonymous")
    queryClient.clear()
  }, [])

  const value = useMemo<SessionContextValue>(() => {
    const scopes = user?.scopes ?? []
    return {
      status,
      user,
      scopes,
      hasScope: (scope) => scopes.includes(scope),
      login,
      logout,
    }
  }, [status, user, login, logout])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export const useSession = (): SessionContextValue => {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error("useSession debe usarse dentro de <SessionProvider>")
  return ctx
}
